document.addEventListener('DOMContentLoaded', () => {
  const panel = document.querySelector('#bythlon-cart-panel');
  if (!panel || typeof panel.showModal !== 'function') return;

  const content = panel.querySelector('[data-cart-content]');
  const errorBox = panel.querySelector('[data-cart-error]');
  const errorMessage = panel.querySelector('[data-cart-error-message]');
  const status = panel.querySelector('[data-cart-status]');
  const closeButton = panel.querySelector('[data-cart-close]');
  const sectionId = panel.dataset.sectionId;
  let busy = false;
  let fresh = true;
  let returnFocus;

  const setBusy = value => {
    busy = value;
    panel.dataset.busy = String(value);
    content.setAttribute('aria-busy', String(value));
    document.querySelectorAll('[data-cart-busy-disabled]').forEach(control => {
      control.disabled = false;
      delete control.dataset.cartBusyDisabled;
    });
    const controls = [...content.querySelectorAll('input, button:not([data-cart-close])')];
    if (value) controls.push(...document.querySelectorAll('.bythlon-buy-form button[type="submit"]'));
    if (value || !fresh) controls.forEach(control => {
      if (!control.disabled) {
        control.dataset.cartBusyDisabled = '';
        control.disabled = true;
      }
    });
  };

  const showError = message => {
    errorMessage.textContent = message;
    errorBox.hidden = false;
    status.textContent = '';
  };

  const clearError = () => {
    errorBox.hidden = true;
    errorMessage.textContent = '';
  };

  const render = html => {
    if (typeof html !== 'string') throw new Error('Missing cart section');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextContent = parsed.querySelector('[data-cart-content]');
    const state = nextContent?.querySelector('[data-cart-state]');
    if (!state) throw new Error('Missing cart state');
    const scrollTop = content.querySelector('.bythlon-cart-panel__items')?.scrollTop || 0;
    content.replaceChildren(...[...nextContent.childNodes].map(node => document.importNode(node, true)));
    const items = content.querySelector('.bythlon-cart-panel__items');
    if (items) items.scrollTop = scrollTop;
    document.querySelectorAll('[data-cart-count]').forEach(count => { count.textContent = state.dataset.count; });
    document.querySelectorAll('[data-cart-open]').forEach(link => { link.setAttribute('aria-label', state.dataset.label); });
    fresh = true;
    setBusy(busy);
    return state.dataset.announcement;
  };

  const fetchCart = async () => {
    const url = new URL(panel.dataset.cartUrl, window.location.origin);
    url.searchParams.set('sections', sectionId);
    const response = await fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error('Cart unavailable');
    const sections = await response.json();
    return render(sections[sectionId]);
  };

  const openPanel = trigger => {
    if (panel.open) return;
    returnFocus = trigger || document.activeElement;
    const nav = document.querySelector('.nav-bar');
    if (nav?.classList.contains('bythlon-menu-open')) {
      nav.classList.remove('bythlon-menu-open');
      document.querySelector('.menu-button')?.setAttribute('aria-expanded', 'false');
      if (returnFocus?.closest('.is-mobile')) returnFocus = document.querySelector('.bythlon-mobile-cart');
    }
    panel.showModal();
    document.documentElement.classList.add('bythlon-cart-open');
    document.querySelectorAll('[data-cart-open]').forEach(link => link.setAttribute('aria-expanded', 'true'));
    closeButton.focus({ preventScroll: true });
  };

  const refresh = async () => {
    if (busy) return;
    clearError();
    status.textContent = panel.dataset.loading;
    setBusy(true);
    try {
      status.textContent = await fetchCart();
    } catch {
      fresh = false;
      showError(panel.dataset.loadError);
    } finally {
      setBusy(false);
    }
  };

  const mutate = async (url, body, trigger) => {
    if (busy) return;
    const focusId = content.contains(trigger) ? trigger.id : content.contains(document.activeElement) ? document.activeElement.id : null;
    openPanel(trigger);
    clearError();
    status.textContent = panel.dataset.updating;
    setBusy(true);
    let serverError;
    try {
      const response = await fetch(url, {
        method: 'POST',
        signal: AbortSignal.timeout(15000),
        headers: { Accept: 'application/json', ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }) },
        body: body instanceof FormData ? body : JSON.stringify(body)
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        if (typeof result.description === 'string') serverError = result.description;
        throw new Error('Cart update rejected');
      }
      try {
        status.textContent = render(result.sections?.[sectionId]);
      } catch {
        status.textContent = await fetchCart();
      }
    } catch {
      fresh = false;
      try { await fetchCart(); } catch {}
      showError(serverError || panel.dataset.changeError);
    } finally {
      setBusy(false);
      if (panel.open && focusId) {
        const target = document.getElementById(focusId) || content.querySelector('[data-cart-quantity]') || closeButton;
        (target.disabled ? closeButton : target).focus({ preventScroll: true });
      }
    }
  };

  const changeQuantity = (input, quantity, trigger) => {
    if (busy || !fresh) return;
    const current = Number(input.dataset.currentQuantity);
    if (!Number.isSafeInteger(quantity) || quantity < 0) {
      input.value = String(current);
      return;
    }
    if (quantity === current) return;
    mutate(panel.dataset.changeUrl, {
      id: input.closest('[data-cart-line]').dataset.key,
      quantity,
      sections: sectionId,
      sections_url: panel.dataset.cartUrl
    }, trigger);
  };

  document.querySelectorAll('[data-cart-open]').forEach(link => {
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', panel.id);
    link.setAttribute('aria-expanded', 'false');
  });

  document.addEventListener('click', event => {
    const link = event.target.closest('[data-cart-open]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    openPanel(link);
    refresh();
  });

  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.matches('[data-cart-form]')) {
      if (busy || !fresh || event.submitter?.name !== 'checkout') event.preventDefault();
      return;
    }
    if (!form.matches('.bythlon-buy-form')) return;
    event.preventDefault();
    if (busy) return;
    const body = new FormData(form);
    body.set('sections', sectionId);
    body.set('sections_url', panel.dataset.cartUrl);
    mutate(panel.dataset.addUrl, body, event.submitter);
  });

  panel.addEventListener('click', event => {
    if (event.target.closest('[data-cart-close]')) {
      panel.close();
      return;
    }
    const control = event.target.closest('[data-cart-step], [data-cart-remove]');
    if (control) {
      const input = control.closest('[data-cart-line]').querySelector('[data-cart-quantity]');
      const quantity = control.hasAttribute('data-cart-remove') ? 0 : Number(input.dataset.currentQuantity) + Number(control.dataset.cartStep);
      changeQuantity(input, quantity, control);
    }
    const bounds = panel.getBoundingClientRect();
    if (event.target === panel && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) panel.close();
  });

  panel.addEventListener('change', event => {
    if (!event.target.matches('[data-cart-quantity]')) return;
    changeQuantity(event.target, event.target.value === '' ? NaN : Number(event.target.value), event.target);
  });

  panel.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-cart-quantity]')) {
      event.preventDefault();
      changeQuantity(event.target, event.target.value === '' ? NaN : Number(event.target.value), event.target);
    }
    if (event.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), [tabindex="0"]')].filter(element => element.tabIndex >= 0 && element.getClientRects().length);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  panel.addEventListener('close', () => {
    document.documentElement.classList.remove('bythlon-cart-open');
    document.querySelectorAll('[data-cart-open]').forEach(link => link.setAttribute('aria-expanded', 'false'));
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  });

  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
});
